import React, { useState } from "react";
import { Modal, Input, Button, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { uploadImageToCloudinary } from "../../helpers/cloudinary"; // Import the Cloudinary helper

// Define the schema for form validation using Zod
const promotionSchema = z.object({
  name: z.string().min(1, "Promotion name is required"),
  description: z.string().min(1, "Description is required"),
  banner: z.string().url("Banner URL must be a valid URL"),
});

type PromotionFormValues = z.infer<typeof promotionSchema>;

interface PromotionModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: PromotionFormValues) => void;
  initialValues?: PromotionFormValues & { id: number }; // Include id in initialValues
}

const PromotionModal: React.FC<PromotionModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PromotionFormValues>({
    resolver: zodResolver(promotionSchema),
    defaultValues: initialValues, // Set default values from initialValues
  });

  const [isUploading, setIsUploading] = useState(false); // Track upload state
  const bannerUrl = watch("banner"); // Watch the banner URL for preview

  // Reset the form when initialValues change
  React.useEffect(() => {
    if (initialValues) {
      reset(initialValues); // Reset the form with new initialValues
    }
  }, [initialValues, reset]);

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const bannerUrl = await uploadImageToCloudinary(file); // Upload to Cloudinary
      setValue("banner", bannerUrl); // Set the banner URL in the form
      message.success("Image uploaded successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      message.error("Failed to upload image. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal
      title="Edit Promotion"
      visible={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit(onSubmit)} // Use handleSubmit to trigger validation
          loading={isUploading} // Disable the button while uploading
        >
          Save
        </Button>,
      ]}
    >
      <form>
        <div className="mb-4">
          <label htmlFor="name">
            Promotion Name <span className="text-red-500">*</span>
          </label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="Enter promotion name"
                status={errors.name ? "error" : ""}
              />
            )}
          />
          {errors.name && <p className="text-red-500">{errors.name.message}</p>}
        </div>

        <div className="mb-4">
          <label htmlFor="description">
            Description <span className="text-red-500">*</span>
          </label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Input.TextArea
                {...field}
                placeholder="Enter description"
                status={errors.description ? "error" : ""}
              />
            )}
          />
          {errors.description && (
            <p className="text-red-500">{errors.description.message}</p>
          )}
        </div>

        <div className="mb-4">
          <label htmlFor="banner">
            Banner Image <span className="text-red-500">*</span>
          </label>
          {bannerUrl && (
            <div className="mb-2">
              <img
                src={bannerUrl}
                alt="Banner Preview"
                className="w-full h-32 object-cover rounded"
              />
            </div>
          )}
          <Upload
            beforeUpload={(file) => {
              handleFileUpload(file); // Handle file upload
              return false; // Prevent default upload behavior
            }}
            showUploadList={false} // Hide the upload list
          >
            <Button icon={<UploadOutlined />} loading={isUploading}>
              Upload Banner Image
            </Button>
          </Upload>
          {errors.banner && (
            <p className="text-red-500">{errors.banner.message}</p>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default PromotionModal;
